import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheckCircle, FiHeart, FiSun } = FiIcons;

const Preparation = () => {
  const basicPreparations = [
    'งดกิจกรรมที่ทำให้ร่างกายอ่อนเพลียในคืนก่อนเดินทาง',
    'รับประทานอาหารเช้าพอประมาณ (50-75% ของปกติ)'
  ];

  const additionalPreparations = [
    'งดอาหารประเภทเนื้อสัตว์ 1-3 วันก่อนพิธี',
    'ฝึกสมาธิหรือสวดมนต์ตามความเชื่อของตน',
    'พักผ่อนให้เพียงพอ'
  ];

  return (
    <section className="py-10 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            การเตรียมกายและใจตามแนวทางโบราณ
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            เพื่อให้การทำพิธีเป็นไปอย่างสมบูรณ์ ขอแนะนำแนวทางปฏิบัติ
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Basic Preparations */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                ข้อปฏิบัติพื้นฐาน
              </h3>
            </div>
            
            <div className="space-y-4">
              {basicPreparations.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Additional Preparations */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiHeart} className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                ข้อปฏิบัติเพิ่มเติม
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4 italic">
              (ไม่บังคับ)
            </p>
            
            <div className="space-y-4">
              {additionalPreparations.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <SafeIcon icon={FiSun} className="w-8 h-8 text-blue-600" />
              <h3 className="text-xl font-semibold text-blue-900">
                สิ่งสำคัญที่ควรจำ
              </h3>
            </div>
            <p className="text-blue-800 leading-relaxed">
              การเตรียมกายและใจไม่ใช่เพียงแค่การปฏิบัติตามกฎเกณฑ์ 
              แต่เป็นการสร้างสภาวะที่เหมาะสมสำหรับการรับพลังงานตามความเชื่อ
              <br />
              <span className="font-semibold">
                ความตั้งใจและความเคารพต่อพิธีกรรมมีความสำคัญมากกว่าการปฏิบัติที่เข้มงวด
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Preparation;